import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import useDebugInformation from "@src/hooks/useDebugInformation"; // Adjust the import path as necessary

import { useAppSelector } from "@store/hooks";
import React, { useEffect, useMemo, useRef } from "react";
import { isGasBoilerServiceJob } from "../Layout/BookingForm/MainForm/Steps/StepThree/RatesTab/DiscountsTab/discountsLogic";
import { FixedPriceBadge } from "./FixedPriceBadge";
import GenericPopupModal from "./GenericPopupModal";
import { RateBadge } from "./RateBadge";

// import { calculateCostAfterVAT } from './../../utilities/general/index';

type ChargesSideBarProps = {
	/**
	 * The identifier for the parent component, used for debugging purposes.
	 */
	parent: string;
};

/**
 * ChargesSideBar Component
 * Renders a sidebar displaying various charges related to a booking, including rates and discounts.
 *
 * @param {ChargesSideBarProps} props - The props for the ChargesSideBar component.
 * @param {string} props.parent - The identifier for the parent component.
 * @returns {React.FC} The ChargesSideBar component.
 */
const ChargesSideBar: React.FC<ChargesSideBarProps> = ({ parent }) => {
	/**
	 * Namespace used for debugging information.
	 */
	const nameSpace = "ChargesSideBar";

	/**
	 * Debugging name constructed from the parent and namespace.
	 */
	const debugName = `[${parent}]▷[${nameSpace}]`;

	/**
	 * Selectors to retrieve necessary state values from the Redux store.
	 */
	const chargeRateAdj = useAppSelector(({ state }) => state.ChargeRateAdj);
	const originalRate = useAppSelector(
		({ state }) => state.ChargeRateOriginalRate,
	);
	const isBusiness = useAppSelector(({ ui }) => ui.PropertyAddressIsBusiness);
	const activeStep = useAppSelector(({ ui }) => ui.ActiveStep);
	const bookingId = useAppSelector(({ ui }) => ui.BookingId);
	const isAGoBack = useAppSelector(({ state }) => state.IsAGoBack);
	const isAQuote = useAppSelector(({ state }) => state.IsAQuote);
	const jobTypeCode = useAppSelector(({ state }) => state.JobTypeCode);
	const charge = useAppSelector(({ state }) => state.Charge);
	const isFixedPrice = useAppSelector(({ state }) => state.IsFixedPrice);
	const chargePriceAdj = useAppSelector(({ state }) => state.ChargePriceAdj);
	const discountId = useAppSelector(
		({ state }) => state.ChargeRateReductionDiscountId,
	);
	const discounted = useAppSelector(({ ui }) => ui.CarePlanReference);

	/**
	 * Ref to track if the generic popup modal has been shown.
	 */
	const hasShownModal = useRef(false);

	/**
	 * Determines if the current scenario is not a go back, not a quote, and not a fixed price.
	 */
	const nonGoBackAndQuoteAndFixedPrice = useMemo(() => {
		return !isAGoBack?.state && !isAQuote && !isFixedPrice;
	}, [isAGoBack?.state, isAQuote, isFixedPrice]);

	/**
	 * Determines if a discount is applied based on the discount ID.
	 */
	const isDiscounted = useMemo(() => {
		return discountId > 0;
	}, [discountId]);

	/**
	 * Determines if the discount is both applied and active.
	 */
	const isDiscountedAndApplied = useMemo(() => {
		return discountId > 0 && discounted.length > 0;
	}, [discountId, discounted]);

	/**
	 * Determines if the current active step is greater than one.
	 */
	const isActiveStepGreaterThanOne = useMemo(() => {
		return activeStep > 1;
	}, [activeStep]);

	/**
	 * Determines if the charge rate has been adjusted.
	 */
	const isChargeRateAdjusted = useMemo(() => {
		return chargeRateAdj !== undefined && chargeRateAdj !== 0;
	}, [chargeRateAdj]);

	/**
	 * Determines if the charge price has been adjusted.
	 */
	const isChargePriceAdjusted = useMemo(() => {
		return chargePriceAdj !== undefined && chargePriceAdj !== 0;
	}, [chargePriceAdj]);

	/**
	 * Determines if the job type is Gas Boiler Service.
	 */
	const isJobTypeGasBoilerService = useMemo(() => {
		return isGasBoilerServiceJob(jobTypeCode);
	}, [jobTypeCode]);

	/**
	 * Determines if the property is not a business.
	 */
	const isNotBusiness = useMemo(() => {
		return !isBusiness;
	}, [isBusiness]);

	/**
	 * Logs the values of the variables for debugging purposes.
	 * These logs can be removed or disabled in production.
	 */
	useEffect(() => {
		console.log("chareRateAdj: ", chargeRateAdj);
		console.log("originalRate: ", originalRate);
		console.log("isBusiness: ", isBusiness);
		console.log("activeStep: ", activeStep);
		console.log("bookingId: ", bookingId);
		console.log("isAGoBack: ", isAGoBack);
		console.log("isAQuote: ", isAQuote);
		console.log("jobTypeCode: ", jobTypeCode);
		console.log("charge: ", charge);
		console.log("isFixedPrice: ", isFixedPrice);
		console.log("chargePriceAdj: ", chargePriceAdj);
		console.log("discountId: ", discountId);
		console.log("discounted: ", discounted);
		console.log(
			"nonGoBackAndQuoteAndFixedPrice: ",
			nonGoBackAndQuoteAndFixedPrice,
		);
		console.log("isDiscounted: ", isDiscounted);
		console.log("isDiscountedAndApplied: ", isDiscountedAndApplied);
		console.log("isActiveStepGreaterThanOne: ", isActiveStepGreaterThanOne);
		console.log("isChargeRateAdjusted: ", isChargeRateAdjusted);
		console.log("isChargePriceAdjusted: ", isChargePriceAdjusted);
		console.log("isJobTypeGasBoilerService: ", isJobTypeGasBoilerService);
		console.log("isNotBusiness: ", isNotBusiness);
	}, [
		chargeRateAdj,
		originalRate,
		isBusiness,
		activeStep,
		bookingId,
		isAGoBack,
		isAQuote,
		jobTypeCode,
		charge,
		isFixedPrice,
		chargePriceAdj,
		discountId,
		discounted,
		nonGoBackAndQuoteAndFixedPrice,
		isDiscounted,
		isDiscountedAndApplied,
		isActiveStepGreaterThanOne,
		isChargeRateAdjusted,
		isChargePriceAdjusted,
		isJobTypeGasBoilerService,
		isNotBusiness,
	]);

	/**
	 * Custom hook to handle debug information logging.
	 */
	useDebugInformation(debugName, { parent });

	/**
	 * Determines if a discounted rate should be displayed based on multiple conditions.
	 */
	const shouldDisplayRateBadge = useMemo(() => {
		return (
			nonGoBackAndQuoteAndFixedPrice &&
			isActiveStepGreaterThanOne &&
			isChargeRateAdjusted &&
			isNotBusiness
		);
	}, [
		nonGoBackAndQuoteAndFixedPrice,
		isActiveStepGreaterThanOne,
		isChargeRateAdjusted,
		isNotBusiness,
	]);

	/**
	 * Determines if a fixed price badge should be displayed based on multiple conditions.
	 */
	const shouldDisplayFixedPriceBadge = useMemo(() => {
		return (
			!nonGoBackAndQuoteAndFixedPrice &&
			isFixedPrice &&
			isActiveStepGreaterThanOne &&
			isChargeRateAdjusted &&
			isNotBusiness &&
			charge?.rate
		);
	}, [
		nonGoBackAndQuoteAndFixedPrice,
		isFixedPrice,
		isActiveStepGreaterThanOne,
		isChargeRateAdjusted,
		isNotBusiness,
		charge,
	]);

	return (
		<Paper elevation={4} sx={{ m: 1, ml: 0, pt: 1, pb: 1 }}>
			{/* Booking Identifier */}
			<Typography variant="h6" sx={{ ml: 1, mr: 1, fontWeight: "bolder" }}>
				BOOKING: {bookingId}
			</Typography>

			{/* Conditional Rendering: Display Domestic or Business Pricing */}
			{nonGoBackAndQuoteAndFixedPrice && isActiveStepGreaterThanOne && (
				<Typography variant="h6" sx={{ ml: 1, mr: 1, fontWeight: "bolder" }}>
					{isNotBusiness
						? "Domestic - Prices ex. VAT"
						: "Business - Prices ex. VAT"}
				</Typography>
			)}

			{/* Conditional Rendering: Discounted Original Rate */}
			{nonGoBackAndQuoteAndFixedPrice &&
				isActiveStepGreaterThanOne &&
				isChargeRateAdjusted &&
				isDiscounted && (
					<Typography variant="h6" sx={{ ml: 1, mr: 1 }}>
						Discounted from: £{originalRate} + VAT
					</Typography>
				)}

			{/* Conditional Rendering: Discounted Charge Price */}
			{!nonGoBackAndQuoteAndFixedPrice &&
				isActiveStepGreaterThanOne &&
				isChargePriceAdjusted &&
				isDiscounted && (
					<Typography variant="h6" sx={{ ml: 1, mr: 1 }}>
						Discounted from: £{charge?.price} + VAT
					</Typography>
				)}

			{/* Conditional Rendering: Current Rate */}
			{nonGoBackAndQuoteAndFixedPrice &&
				isActiveStepGreaterThanOne &&
				isChargeRateAdjusted && (
					<Typography variant="h6" sx={{ ml: 1, mr: 1 }}>
						{isDiscounted ? "NEW " : ""}Rate: £{chargeRateAdj} + VAT
					</Typography>
				)}

			{/* Conditional Rendering: Fixed Price */}
			{isFixedPrice && isActiveStepGreaterThanOne && isChargeRateAdjusted && (
				<Typography variant="h6" sx={{ ml: 1, mr: 1 }}>
					{isDiscounted ? "NEW " : ""}Fixed Price: £{chargePriceAdj} + VAT
				</Typography>
			)}

			{/* Conditional Rendering: RateBadge Component */}
			{shouldDisplayRateBadge && charge?.rate && (
				<RateBadge discounted={isDiscountedAndApplied} charge={charge} />
			)}

			{/* Conditional Rendering: FixedPriceBadge Component */}
			{shouldDisplayFixedPriceBadge && (
				<FixedPriceBadge discounted={isDiscountedAndApplied} />
			)}

			{/* Conditional Rendering: Go Back Information */}
			{isAGoBack?.state && (
				<Typography variant="h6" sx={{ ml: 1, mr: 1, fontWeight: "bolder" }}>
					Go Back For: {isAGoBack.previousJobId}
				</Typography>
			)}

			{/* Conditional Rendering: Generic Popup Modal */}
			{hasShownModal.current && (
				<GenericPopupModal
					modalTitle="Attention Required"
					adviseList={[
						"18 months billing cycle is only available through Direct Debit, monthly and quarterly are supported too!",
					]}
					buttonText="Close Message"
				/>
			)}
		</Paper>
	);
};

export default React.memo(ChargesSideBar);
